'use client'

import * as React from 'react'
import { useInstantSearch, useMenu, useSearchBox } from 'react-instantsearch'
import type { MenuItem } from 'instantsearch.js/es/connectors/menu/connectMenu'
import { uniqBy } from 'utils/array'
import { algoliaTemplatesIndex } from 'algolia/algolia-templates'
import { useCategories } from 'algolia/algolia.components'
import { Separator } from 'primitives/separator'
import { tw } from 'utils/classnames'
import { NavigationBarTemplates } from './navigation-bar-templates'
import { searchForFacets, transformItems, useSelectedCategory } from './utils'
function getFilterString(categoryLabel: string, tags: string[] | undefined) {
  let filterString = ''
  if (categoryLabel) {
    filterString += `category:"${categoryLabel}"`
  }
  if (tags) {
    if (filterString) {
      filterString += ' AND '
    }
    filterString += tags.map(tag => `tags:"${tag}"`).join(' OR ')
  }

  return filterString
}

interface NavigationSidebarProps extends React.ComponentProps<'div'> {
  subcategoriesText: string
  allTemplatesText: string
  favoritesText?: string
  isFavorites?: boolean
  selectedClassName?: string
  customCategory?: string
  customQuery?: string
  handleClick?: (value?: string) => void
}

export function NavigationSidebar({
  allTemplatesText,
  subcategoriesText,
  favoritesText,
  isFavorites = false,
  className,
  selectedClassName,
  customCategory,
  customQuery,
  handleClick,
}: NavigationSidebarProps) {
  const { items: categoryItems, refine: refineCategory } = useMenu({
    attribute: 'category',
    transformItems,
  })
  const { items: subcategoryItems, refine: refineSubcategory } = useMenu({
    attribute: 'subcategory',
    transformItems,
  })

  const [allCategories, setAllCategories] = React.useState<MenuItem[]>([])
  const [allSubcategories, setAllSubcategories] = React.useState<MenuItem[]>([])
  const custom = React.useRef({
    category: customCategory,
    query: customQuery,
    hasRun: false,
  })
  const selectedCategory = useSelectedCategory()
  const selectedSubcategory = useSelectedCategory({ isSubcategory: true })
  const { category: categoryLabel, subcategory: subcategoryLabel } = useCategories()

  const { uiState } = useInstantSearch()
  const query = uiState?.[algoliaTemplatesIndex]?.query
  const tags = uiState?.[algoliaTemplatesIndex]?.refinementList?.tags

  function handleRefineCategory(value: string) {
    if (subcategoryLabel) {
      refineSubcategory(subcategoryLabel)
    }
    refineCategory(value || categoryLabel)
  }
  const { refine: refineSearch } = useSearchBox()

  // prevent redundant refinements to avoid state resets if custom category is already applied
  const isAlreadyRefined = React.useMemo(
    () => categoryItems?.some(item => item.isRefined && item.value === custom.current.category),
    [categoryItems],
  )

  const isSameQuery = uiState?.[algoliaTemplatesIndex]?.query === custom.current.query

  React.useEffect(() => {
    if (custom.current.hasRun) {
      return
    }
    custom.current.hasRun = true

    if (custom.current.category && !isAlreadyRefined) {
      refineCategory(custom.current.category)
    }

    if (custom.current.query && isSameQuery) {
      refineSearch(custom.current.query)
    }
    // run only once when custom category and query is passed from homepage search
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [
    custom.current.category,
    custom.current.query,
    isAlreadyRefined,
    refineCategory,
    refineSearch,
  ])

  React.useEffect(() => {
    searchForFacets({
      query: query || '',
      filters: tags?.map(tag => `tags:"${tag}"`).join(' OR '),
    }).then(setAllCategories)
  }, [query, tags])

  React.useEffect(() => {
    searchForFacets({
      facetName: 'subcategory',
      filters: getFilterString(categoryLabel, tags),
      query: query || '',
    }).then(setAllSubcategories)
  }, [query, tags, categoryLabel])

  return (
    <div className={tw('max-w-[289px] p-4', className)}>
      <NavigationBarTemplates.Root
        isFavorites={isFavorites}
        attribute="category"
        onRefine={(value: string, link?: string) => {
          handleRefineCategory(value)
          handleClick?.(link)
        }}
      >
        {favoritesText ? (
          <>
            <NavigationBarTemplates.MenuFavoritesButton
              title={favoritesText}
              selectedClassName={selectedClassName}
            />
            <Separator className="mb-[14px] mt-[10px]" />
          </>
        ) : null}

        <NavigationBarTemplates.MenuHeaderButton
          selectedClassName={selectedClassName}
          allTitle={allTemplatesText}
        />
        <NavigationBarTemplates.MenuItems
          selectedClassName={selectedClassName}
          items={transformItems(
            uniqBy([...categoryItems, ...allCategories, selectedCategory], 'label'),
          )}
        />
      </NavigationBarTemplates.Root>

      <NavigationBarTemplates.Root
        attribute="subcategory"
        onRefine={(value: string, link?: string) => {
          refineSubcategory(value)
          handleClick?.(link)
        }}
      >
        <h2 className="text-md font-semibold leading-10 text-gray-200">{subcategoriesText}</h2>

        <NavigationBarTemplates.MenuItems
          selectedClassName={selectedClassName}
          items={transformItems(
            uniqBy([...subcategoryItems, ...allSubcategories, selectedSubcategory], 'label'),
          )}
        />
      </NavigationBarTemplates.Root>
    </div>
  )
}
